// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { useEffect, useRef, useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { arePaymentsVisibleOnPlatform } from "@/native/ios-payments-gate";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PssmngrCurrentPlan } from "@/components/dashboard/settings/billing/PssmngrCurrentPlan";
import { PssmngrAddOns } from "@/components/dashboard/settings/billing/PssmngrAddOns";
import { PssmngrPlanComparison } from "@/components/dashboard/settings/billing/PssmngrPlanComparison";
import { PssmngrInvoiceHistory } from "@/components/dashboard/settings/billing/PssmngrInvoiceHistory";
import { pssmngrBillingKeys } from "@/hooks/billing/usePssmngrBilling";
import { normaliseBillingSearch } from "@/lib/billing/normalise-search";

export const Route = createFileRoute(
	"/_app/_auth/dashboard/settings/billing",
)({
	// Payments disabled → the billing page does not exist. Redirect any direct
	// navigation back to the settings index so there is no reachable billing UI.
	// Also redirects on iOS native when VITE_NATIVE_IAP_ENABLED_IOS=false
	// (App Store guideline 3.1.1: no checkout inside the iOS app — applies to
	// any web provider, not just Stripe).
	beforeLoad: () => {
		if (!arePaymentsVisibleOnPlatform()) {
			throw redirect({ to: "/dashboard/settings" });
		}
	},
	component: BillingSettingsPage,
	validateSearch: normaliseBillingSearch,
});

function BillingSettingsPage() {
	const { t } = useTranslation("billing");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { status, purchase, organization_id } = Route.useSearch();
	const toastFiredRef = useRef(false);
	const [checkoutStatus, setCheckoutStatus] = useState<typeof status>(status);
	const [checkoutPurchase, setCheckoutPurchase] =
		useState<typeof purchase>(purchase);

	useEffect(() => {
		if (toastFiredRef.current || !status) return;
		toastFiredRef.current = true;
		setCheckoutStatus(status);
		setCheckoutPurchase(purchase);

		if (status === "success") {
			queryClient.invalidateQueries({
				queryKey: pssmngrBillingKeys.all,
			});
			toast.success(
				purchase === "addon"
					? t("checkout.addOnReceived")
					: t("checkout.syncError"),
			);
		} else if (status === "canceled") {
			toast.info(
				t(
					"checkout.canceled",
					"Checkout was canceled. No charges were made.",
				),
			);
		}

		// Clear ALL provider-specific return params so a reload doesn't replay
		// the toast, regardless of which provider sent us back.
		navigate({
			to: "/dashboard/settings/billing",
			search: {
				status: undefined,
				purchase: undefined,
				addOnId: undefined,
				organization_id: undefined,
				order_id: undefined,
				checkout_id: undefined,
			},
			replace: true,
		});
	}, [status, purchase, navigate, t, queryClient]);

	return (
		<div className="space-y-6">
			<h2 className="text-2xl font-semibold">
				{t("page.title", "Billing")}
			</h2>

			{checkoutStatus && (
				<div
					role="status"
					className={
						checkoutStatus === "success"
							? "rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100"
							: "rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
					}
				>
					{checkoutStatus === "success"
						? checkoutPurchase === "addon"
							? t("checkout.addOnReceived")
							: t("checkout.syncError")
						: t(
							"checkout.canceled",
							"Checkout was canceled. No charges were made.",
						)}
				</div>
			)}

			<PssmngrCurrentPlan />
			<PssmngrAddOns initialOrganizationId={organization_id} />
			<PssmngrPlanComparison />
			<PssmngrInvoiceHistory />
		</div>
	);
}
